import type { ColumnDef } from "@tanstack/react-table"
import { useMemo } from "react"

import type { DestinationOverride } from ".."
import {
  GoodsReceiptPalletActionsCell,
  GoodsReceiptPalletAs400SyncStatusCell,
  GoodsReceiptPalletBatchNumbersCell,
  GoodsReceiptPalletCurrentLocationCell,
  GoodsReceiptPalletDestinationCell,
  GoodsReceiptPalletLotNumbersCell,
  GoodsReceiptPalletLpnCell,
  GoodsReceiptPalletProductQtyCell,
  GoodsReceiptPalletReferenceIdCell,
} from "../cells"

import { PermissionActionType } from "@/constants/permissions"
import type { GoodsReceiptPallet, WarehouseArea } from "@/graphql/codegen/graphql"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { isAkrochemManagedWarehouse } from "@/screens/WarehouseReceiving/utils"

export interface UseGoodsReceiptPalletsEditableColumnsParams {
  destinationOverrides?: Map<string, DestinationOverride>
  warehouse?: WarehouseArea | null
  isSyncPending?: boolean
  canMaintain: boolean
  onDestinationOverride?: (pallet: GoodsReceiptPallet) => void
  onLpnEdit?: (pallet: GoodsReceiptPallet) => void
  onQrPrint?: (pallet: GoodsReceiptPallet) => void
  onDelete?: (pallet: GoodsReceiptPallet) => void
}

export const useGoodsReceiptPalletsEditableColumns = ({
  destinationOverrides,
  warehouse,
  isSyncPending,
  canMaintain,
  onDestinationOverride,
  onLpnEdit,
  onQrPrint,
  onDelete,
}: UseGoodsReceiptPalletsEditableColumnsParams): ColumnDef<GoodsReceiptPallet>[] => {
  const isAkrochemManaged = isAkrochemManagedWarehouse(warehouse?.type)
  const { hasPermissions } = usePermissionManager()

  return useMemo<ColumnDef<GoodsReceiptPallet>[]>(() => {
    const coreColumns: ColumnDef<GoodsReceiptPallet>[] = [
      {
        accessorKey: "palletQty",
        header: "Product / Qty",
        cell: ({ row }) => <GoodsReceiptPalletProductQtyCell pallet={row.original} />,
      },
      {
        accessorKey: "lots",
        header: "Lot #",
        enableSorting: false,
        cell: ({ row }) => <GoodsReceiptPalletLotNumbersCell pallet={row.original} />,
      },
      {
        accessorKey: "batchNumber",
        header: "Batch #",
        size: 80,
        enableSorting: false,
        cell: ({ row }) => (
          <GoodsReceiptPalletBatchNumbersCell goodsReceiptPalletRow={row.original.goodsReceiptPalletRow!} />
        ),
      },
      {
        accessorKey: "currentLocation",
        header: "Current Location",
        enableSorting: false,
        cell: ({ row }) => <GoodsReceiptPalletCurrentLocationCell pallet={row.original} />,
      },
      {
        accessorKey: destinationOverrides ? "suggestedDestinationWarehouseShelving" : "currentLocation",
        header: "Destination",
        enableSorting: false,
        cell: ({ row }) => {
          const destination = destinationOverrides
            ? destinationOverrides.get(row.original.goodsReceiptPalletId)
            : row.original.destinationWarehouseShelving

          const shouldShowEdit =
            hasPermissions([PermissionActionType.LOCATION_OVERRIDE]) && !!warehouse && !!destinationOverrides

          return (
            <GoodsReceiptPalletDestinationCell
              pallet={row.original}
              destination={destination}
              shouldShowEdit={shouldShowEdit}
              isEditDisabled={isSyncPending}
              onOverride={() => onDestinationOverride?.(row.original)}
            />
          )
        },
      },
      {
        accessorKey: "referenceId",
        header: "Reference ID",
        size: 100,
        cell: ({ row }) => <GoodsReceiptPalletReferenceIdCell pallet={row.original} />,
      },
      {
        accessorKey: "as400SyncStatus",
        header: "",
        enableSorting: false,
        cell: ({ row }) => <GoodsReceiptPalletAs400SyncStatusCell pallet={row.original} />,
      },
    ]

    const lpnColumn: ColumnDef<GoodsReceiptPallet> = {
      accessorKey: "lpn",
      header: "LPN",
      cell: ({ row }) => (
        <GoodsReceiptPalletLpnCell
          pallet={row.original}
          canMaintain={canMaintain}
          isEditDisabled={isSyncPending}
          onEdit={() => onLpnEdit?.(row.original)}
        />
      ),
    }

    const actionsColumn: ColumnDef<GoodsReceiptPallet> = {
      id: "actions",
      header: "",
      size: 100,
      enableSorting: false,
      cell: ({ row }) => (
        <GoodsReceiptPalletActionsCell
          pallet={row.original}
          shouldShowQrPrint={isAkrochemManaged}
          shouldShowDelete={canMaintain}
          isDeleteDisabled={isSyncPending}
          onQrPrint={() => onQrPrint?.(row.original)}
          onDelete={() => onDelete?.(row.original)}
        />
      ),
    }

    return [
      ...coreColumns,
      ...(isAkrochemManaged ? [] : [lpnColumn]),
      ...(isAkrochemManaged || canMaintain ? [actionsColumn] : []),
    ]
  }, [
    destinationOverrides,
    warehouse,
    isSyncPending,
    canMaintain,
    isAkrochemManaged,
    hasPermissions,
    onDestinationOverride,
    onLpnEdit,
    onQrPrint,
    onDelete,
  ])
}
